import React, { useEffect, useMemo, useState } from 'react';
import { EDUCATION_OPTIONS, REFERENCE_OPTIONS } from '../../lib/calculator';
import PatientPicker from './PatientPicker';
import BlockAssessmentModal from './BlockAssessmentModal';
import { useLocale } from '../LocaleProvider';

export function todayDateOnly(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function isFutureApplicationDate(value, today = todayDateOnly()) {
  return Boolean(value) && value > today;
}

function parseAnswers(value) {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function buildManagementAssessmentPayload({
  score,
  appliedAt,
  referenceKey,
  observation,
  mode,
  education,
  ageConfirm,
  existingAnswers = {},
  blockScores = {},
}) {
  return {
    score: Number(score),
    applied_at: appliedAt,
    instrument_version: 'meem-br-2003-operationalized',
    reference_key: referenceKey || null,
    observation: String(observation || '').trim() || null,
    answers_json: JSON.stringify({
      ...parseAnswers(existingAnswers),
      mode,
      education,
      ageConfirm,
      ...(Object.keys(blockScores || {}).length ? { blockScores } : {}),
    }),
  };
}

export default function ManagementAssessmentForm({
  patients = [],
  initialPatientId = '',
  initialAssessment = null,
  onSaved,
  onCancel,
}) {
  const { locale } = useLocale();
  const es = locale === 'es';
  const today = useMemo(() => todayDateOnly(), []);
  const initialAnswers = useMemo(() => parseAnswers(initialAssessment?.answers_json), [initialAssessment]);
  const [patientId, setPatientId] = useState(String(initialAssessment?.patient_id || initialPatientId || ''));
  const [appliedAt, setAppliedAt] = useState(String(initialAssessment?.applied_at || today).slice(0, 10));
  const [score, setScore] = useState(initialAssessment?.score === undefined ? '' : String(initialAssessment.score));
  const [referenceKey, setReferenceKey] = useState(initialAssessment?.reference_key || 'brucki');
  const [observation, setObservation] = useState(initialAssessment?.observation || '');
  const [mode, setMode] = useState(initialAnswers.mode || 'total');
  const [education, setEducation] = useState(initialAnswers.education || 'none');
  const [ageConfirm, setAgeConfirm] = useState(initialAnswers.ageConfirm || 'not_informed');
  const [blockScores, setBlockScores] = useState(initialAnswers.blockScores || {});
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const selectedPatient = patients.find((patient) => String(patient.id) === String(patientId));
  const editing = Boolean(initialAssessment?.id);

  // A pontuação e as respostas não são guardadas no navegador (a política de
  // privacidade promete isso). Versões anteriores gravavam um rascunho aqui;
  // apaga o que tiver ficado para trás.
  useEffect(() => {
    try {
      for (const storage of [window.localStorage, window.sessionStorage]) {
        Object.keys(storage).filter((key) => key.startsWith('minimental:assessment-draft:')).forEach((key) => storage.removeItem(key));
      }
    } catch { /* armazenamento local pode estar indisponível */ }
  }, []);

  async function submit(event) {
    event.preventDefault();
    setError('');
    setSuccess('');

    const numericScore = Number(score);
    if (!patientId) {
      setError(es ? 'Selecciona una persona antes de guardar.' : 'Selecione uma pessoa antes de salvar.');
      return;
    }
    if (!Number.isInteger(numericScore) || numericScore < 0 || numericScore > 30) {
      setError(es ? 'Introduce una puntuación entera entre 0 y 30.' : 'Informe uma pontuação inteira entre 0 e 30.');
      return;
    }
    if (!appliedAt) {
      setError(es ? 'Introduce la fecha de aplicación.' : 'Informe a data em que a avaliação foi aplicada.');
      return;
    }
    if (isFutureApplicationDate(appliedAt, today)) {
      setError(es ? 'La fecha de aplicación no puede ser futura.' : 'A data de aplicação não pode ser futura.');
      return;
    }

    setBusy(true);
    try {
      const endpoint = editing
        ? `/api/management/assessments/${encodeURIComponent(initialAssessment.id)}`
        : `/api/management/patients/${encodeURIComponent(patientId)}/assessments`;
      const response = await fetch(endpoint, {
        method: editing ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(buildManagementAssessmentPayload({
          score: numericScore,
          appliedAt,
          referenceKey,
          observation,
          mode,
          education,
          ageConfirm,
          blockScores,
          existingAnswers: initialAnswers,
        })),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(es ? 'No fue posible guardar la evaluación.' : (payload.error || 'Não foi possível salvar a avaliação.'));
      window.eidocAnalytics?.capture(editing ? 'assessment_updated' : 'assessment_created', { mode, has_observation: Boolean(String(observation || '').trim()), has_block_scores: Object.keys(blockScores || {}).length > 0 });
      setSuccess(es ? (editing ? 'Evaluación actualizada.' : `Evaluación de ${selectedPatient?.display_name || 'la persona atendida'} guardada.`) : (editing ? 'Avaliação atualizada.' : `Avaliação de ${selectedPatient?.display_name || 'pessoa acompanhada'} salva.`));
      onSaved?.(payload);
    } catch (requestError) {
      setError(es ? 'No fue posible guardar la evaluación.' : (requestError.message || 'Não foi possível salvar a avaliação.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} aria-labelledby="management-assessment-title" className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold text-[var(--text-muted)]">{es ? 'Registro rápido' : 'Registro rápido'}</p>
          <h2 id="management-assessment-title" className="mt-1 text-2xl font-semibold text-[var(--primary)]">{es ? (editing ? 'Editar evaluación' : 'Nueva evaluación') : (editing ? 'Editar avaliação' : 'Nova avaliação')}</h2>
          <p className="mt-1 text-sm text-[var(--text-muted)]">{es ? (editing ? 'Corrige o completa este registro histórico.' : 'Añade una aplicación anterior o de hoy al historial de la persona.') : (editing ? 'Corrija ou complete este registro histórico.' : 'Inclua uma aplicação anterior ou de hoje no histórico da pessoa.')}</p>
        </div>
        {onCancel && (
          <button type="button" onClick={onCancel} className="rounded-lg px-2 py-1 text-2xl leading-none text-[var(--text-muted)]" aria-label={es ? 'Cerrar registro' : 'Fechar registro'}>
            ×
          </button>
        )}
      </div>

      {error && <p role="alert" className="rounded-xl bg-[var(--danger-bg)] px-4 py-3 text-sm font-semibold text-[var(--danger-text)]">{error}</p>}
      {success && <p role="status" className="rounded-xl bg-[var(--success-bg)] px-4 py-3 text-sm font-semibold text-[var(--success-text)]">{success}</p>}

      {es && <p role="note" lang="es" className="rounded-xl border border-[var(--warning-text)] bg-[var(--warning-bg)] px-4 py-3 text-sm leading-relaxed text-[var(--warning-text)]">El instrumento y sus instrucciones corresponden a la versión brasileña en portugués (PT-BR). Esta interfaz en español solo ayuda a registrar la puntuación; no es una versión traducida ni validada del MMSE.</p>}

      <PatientPicker patients={patients} value={patientId} onChange={setPatientId} disabled={busy || editing} />

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block text-sm font-bold text-[var(--primary)]">
          {es ? 'Puntuación total' : 'Pontuação total'}
          <input required type="number" min="0" max="30" step="1" inputMode="numeric" value={score} onChange={(event) => setScore(event.target.value)} className="input-field mt-2 text-lg font-bold" placeholder={es ? '0 a 30' : '0 a 30'} />
        </label>
        <label className="block text-sm font-bold text-[var(--primary)]">
          {es ? 'Fecha de aplicación' : 'Data da aplicação'}
          <input required type="date" max={today} value={appliedAt} onChange={(event) => setAppliedAt(event.target.value)} className="input-field mt-2" />
          <span className="mt-1 block text-xs font-normal text-[var(--text-muted)]">{es ? 'Se aceptan fechas pasadas.' : 'Datas passadas são aceitas.'}</span>
        </label>
      </div>

      <label className="block text-sm font-bold text-[var(--primary)]">
        {es ? 'Referencia utilizada' : 'Referência utilizada'}
        <select value={referenceKey} onChange={(event) => setReferenceKey(event.target.value)} className="input-field mt-2">
          {REFERENCE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </label>

      <label className="block text-sm font-bold text-[var(--primary)]">
        {es ? 'Observación' : 'Observação'} <span className="font-normal text-[var(--text-muted)]">({es ? 'opcional' : 'opcional'})</span>
        <textarea value={observation} maxLength={4000} onChange={(event) => setObservation(event.target.value)} className="input-field mt-2 min-h-24 resize-y" placeholder={es ? 'Contexto clínico u observación del registro' : 'Contexto clínico ou observação do registro'} />
      </label>

      <details className="rounded-xl border border-[var(--border)] bg-[var(--surface-soft)] px-4 py-3">
        <summary className="cursor-pointer text-sm font-bold text-[var(--primary)]">{es ? 'Detalles de la aplicación' : 'Detalhes da aplicação'}</summary>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <label className="block text-sm font-semibold text-[var(--primary)]">
            {es ? 'Forma de registro' : 'Forma de registro'}
            <select value={mode} onChange={(event) => { const next = event.target.value; setMode(next); if (next === 'domains') setShowBlockModal(true); }} className="input-field mt-2 text-sm">
              <option value="total">{es ? 'Puntuación total' : 'Pontuação total'}</option>
              <option value="domains">{es ? 'Por dominios' : 'Por blocos'}</option>
            </select>
          </label>
          <label className="block text-sm font-semibold text-[var(--primary)]">
            {es ? 'Escolaridad' : 'Escolaridade informada'}
            <select value={education} onChange={(event) => setEducation(event.target.value)} className="input-field mt-2 text-sm">
              {EDUCATION_OPTIONS.map((option) => {
                const labelsEs = { noFormalEducation: 'Sin escolaridad formal', years1To4: '1 a 4 años de estudios', years5To8: '5 a 8 años de estudios', years9To11: '9 a 11 años de estudios', years12OrMore: '12 años o más de estudios', none: 'Prefiero no informar' };
                return <option key={option.value} value={option.value}>{es ? labelsEs[option.value] : option.label}</option>;
              })}
            </select>
          </label>
          {referenceKey === 'lourenco' && (
            <label className="block text-sm font-semibold text-[var(--primary)] sm:col-span-2">
              {es ? '¿Más de 65 años?' : 'Mais de 65 anos?'}
              <select value={ageConfirm} onChange={(event) => setAgeConfirm(event.target.value)} className="input-field mt-2 text-sm">
                <option value="yes">{es ? 'Sí' : 'Sim'}</option>
                <option value="no">{es ? 'No' : 'Não'}</option>
                <option value="not_informed">{es ? 'No informado' : 'Não informado'}</option>
              </select>
            </label>
          )}
        </div>
      </details>

      <div className="flex flex-wrap justify-end gap-3 border-t border-[var(--border)] pt-4">
        {onCancel && <button type="button" onClick={onCancel} disabled={busy} className="rounded-xl border border-[var(--border)] px-5 py-3 font-bold text-[var(--primary)]">{es ? 'Cancelar' : 'Cancelar'}</button>}
        <button type="submit" disabled={busy || !patients.length} className="rounded-xl bg-[var(--accent)] px-5 py-3 font-bold text-[var(--on-accent)] disabled:cursor-wait disabled:opacity-60">
          {busy ? (es ? 'Guardando…' : 'Salvando…') : es ? (editing ? 'Guardar cambios' : 'Guardar evaluación') : editing ? 'Salvar alterações' : 'Salvar avaliação'}
        </button>
      </div>
      {showBlockModal && (
        <BlockAssessmentModal
          scores={blockScores}
          onChange={(domainId, value) => {
            const next = { ...blockScores, [domainId]: value };
            setBlockScores(next);
            const total = Object.values(next).reduce((sum, item) => sum + (Number(item) || 0), 0);
            setScore(String(total));
          }}
          onClose={() => setShowBlockModal(false)}
          onConfirm={(total) => { setScore(String(total)); setShowBlockModal(false); }}
        />
      )}
    </form>
  );
}
